import React from "react";

/*
 * Shared flow primitives for the product diagrams. Matches the presentation's
 * look: a faint static connector plus small glowing "packets" that travel ALONG
 * the path (SVG animateMotion + mpath) - crisp and directional, unlike an
 * animated stroke-dash. Packets are omitted under reduced motion (the static
 * connector still renders, so the diagram reads fine).
 */

// Soft glow filter for the packets. `id` must be unique per diagram (SVG ids are
// document-global), so each diagram passes its own.
export const FlowGlowDefs: React.FC<{ id: string }> = ({ id }) => (
  <defs>
    <filter id={id} x="-70%" y="-70%" width="240%" height="240%">
      <feGaussianBlur stdDeviation="1.8" result="b" />
      <feMerge>
        <feMergeNode in="b" />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  </defs>
);

export interface FlowPathProps {
  id: string; // unique path id (also referenced by mpath)
  d: string;
  glowId: string;
  reduced: boolean;
  color?: string;
  base?: string; // static connector color
  count?: number; // packets per path
  dur?: number; // seconds for one traversal
  r?: number; // packet radius
}

export const FlowPath: React.FC<FlowPathProps> = ({
  id,
  d,
  glowId,
  reduced,
  color = "var(--ak-accent-2)",
  base = "var(--ak-on-navy-border)",
  count = 2,
  dur = 3.2,
  r = 3.5,
}) => (
  <g>
    <path id={id} d={d} fill="none" stroke={base} strokeWidth={1} />
    {!reduced &&
      Array.from({ length: count }).map((_, k) => (
        <circle key={k} r={r} fill={color} filter={`url(#${glowId})`}>
          <animateMotion
            dur={`${dur}s`}
            begin={`${-(dur / count) * k}s`}
            repeatCount="indefinite"
            keyPoints="0;1"
            keyTimes="0;1"
            calcMode="linear"
          >
            <mpath href={`#${id}`} />
          </animateMotion>
        </circle>
      ))}
  </g>
);
